/* Scoped to sections containing a Product options form block. No global fetch overrides. */
(() => {
  'use strict';
  const pants = {type:'images', label:'Types Of Pants', required:true, options:'Straight Pants | 0\nJogger Pants | 0\n5 Pocket Cargos | 0\nWide Legs | 0\nCoco Wide Leg | 0'};
  // Dropdown contents were not visible in the references. These editable sample labels must be reviewed.
  const shirt = {type:'select', label:'Select Sleeve Style For Shirt', required:true, placeholder:'Select Sleeves', options:'Short sleeves | 0\nLong sleeves | 0'};
  const engraving = price => ({type:'text',label:'Personalize Name Engraving',placeholder:'Write Your Name',options:`Engraving | ${price}`});
  const size = {type:'textarea',label:'Specify your Custom Size',placeholder:'Enter your measurements',options:'Custom size | 700'};
  const cap = {type:'radio',label:'Add a Surgical Cap',required:true,options:'Yes | 600\nNo | 0'};
  const presets = {
    basic:[pants,shirt,engraving(350)],
    complete:[pants,shirt,size,engraving(350),{type:'checkbox',label:'Add Underscrubs',options:'White | 2000\nBlack | 2000\nNope | 0'}],
    cap300:[pants,{...shirt,label:'Type of shirt'},cap,size,engraving(300)],
    accessories:[{type:'radio',label:'Would you like a matching mask?',required:true,options:'Yes | 300\nNo | 0'},engraving(350),{type:'checkbox',label:'Add pen pocket',options:'Yes | 150'}],
    cap350:[pants,{...shirt,label:'Type of shirt'},cap,size,engraving(350)],
    size300:[pants,{...shirt,label:'Type of shirt'},size,engraving(300)],
    custom:[]
  };
  const money = (amount,currency) => new Intl.NumberFormat(undefined,{style:'currency',currency}).format(amount/100);
  function parseOptions(text) {
    return String(text || '').split(/\r?\n/).map(s=>s.trim()).filter(Boolean).map(line=>{
      const parts=line.split('|').map(s=>s.trim());
      if(parts.length>4 || !parts[0]) throw new Error('Use option rows: Label | Price | Variant ID | Image URL.');
      const price=parts[1] || '0';
      if(!/^\d+(?:\.\d{1,2})?$/.test(price)) throw new Error(`Invalid price for ${parts[0]}. Use a positive number or 0, without currency symbols.`);
      const amount=Math.round(Number(price)*100);
      if(!Number.isSafeInteger(amount)) throw new Error('Option price is too large.');
      const variantId=parts[2] || '';
      if(variantId && !/^\d+$/.test(variantId)) throw new Error(`Invalid variant ID for ${parts[0]}.`);
      const image=parts[3] || '';
      if(image && !/^https:\/\//i.test(image)) throw new Error('Image URLs must start with https://.');
      return {label:parts[0],amount,variantId,image};
    });
  }
  function buildFields(config) {
    const base=presets[config.preset] || presets.custom;
    const fields=[];
    const names=new Set();
    for(let i=0;i<6;i++) {
      const override=config.fields?.[i] || {};
      const source=base[i] || {type:'off',label:`Field ${i+1}`,options:''};
      const type=!override.type || override.type==='auto' ? source.type : override.type;
      if(type==='off') continue;
      if(!['images','select','radio','checkbox','text','textarea'].includes(type)) throw new Error('Unknown field type.');
      const field={...source,type,index:i,label:override.label?.trim() || source.label,
        defaultOption:Number(override.defaultOption || 0),
        placeholder:override.placeholder || source.placeholder || '',
        required:override.required==='yes' ? true : override.required==='no' ? false : !!source.required,
        options:parseOptions(override.options?.trim() || source.options)};
      if(!field.label || /[\[\]]/.test(field.label) || field.label.startsWith('_') || names.has(field.label)) throw new Error('Field names must be unique, visible names without square brackets.');
      names.add(field.label);
      if(!field.options.length) {
        if(['text','textarea'].includes(type)) field.options=[{label:field.label,amount:0,variantId:'',image:''}];
        else throw new Error(`Add at least one option for ${field.label}.`);
      }
      if(['text','textarea'].includes(type) && field.options.length!==1) throw new Error(`${field.label}: text fields need exactly one fee row.`);
      if(new Set(field.options.map(o=>o.label)).size!==field.options.length) throw new Error(`${field.label}: option names must be unique.`);
      fields.push(field);
    }
    if(!fields.length) throw new Error('Configure at least one field in the Product options form block.');
    return fields;
  }
  function resolveOption(option,config) {
    if(!option.variantId) {
      return {...option,price:option.amount,error:option.amount>0 ? 'This paid option is not configured yet. Please contact the store.' : ''};
    }
    const variant=config.addons.find(v=>String(v.id)===option.variantId);
    if(!variant) return {...option,price:option.amount,error:'The add-on product for this option has not been configured.'};
    if(config.variants.some(v=>String(v.id)===option.variantId)) return {...option,price:option.amount,error:'Use a separate add-on product for paid options.'};
    if(!variant.available) return {...option,price:variant.price,error:'This add-on is currently sold out.'};
    if(config.currency===config.storeCurrency && variant.price!==option.amount) return {...option,price:variant.price,error:'The configured option price does not match its Shopify price. Please contact the store.'};
    return {...option,price:variant.price,error:''};
  }
  function selectionSummary(selections,config) {
    const properties=Object.create(null), addons=[];
    let surcharge=0;
    for(const {field,values,text} of selections) {
      if(!values.length) continue;
      const selected=values.map(o=>resolveOption(o,config));
      const invalid=selected.find(o=>o.error);
      if(invalid) throw new Error(`${field.label}: ${invalid.error}`);
      properties[field.label]=text || selected.map(o=>o.label+(o.price ? ` (+${money(o.price,config.currency)})` : '')).join(', ');
      for(const option of selected) {
        surcharge+=option.price;
        if(option.variantId) addons.push({id:option.variantId,field:field.label,value:text || option.label,price:option.price});
      }
    }
    return {properties,addons,surcharge};
  }
  function buildCartData(original,variantId,quantity,summary,productTitle,group) {
    const data=new FormData();
    data.set('items[0][id]',variantId);data.set('items[0][quantity]',String(quantity));
    // Preserve existing properties, including uploaded files and gift-card fields.
    for(const [key,value] of original) {
      const match=key.match(/^properties\[(.+)\]$/);
      if(match) data.append(`items[0][properties][${match[1]}]`,value);
    }
    for(const [key,value] of Object.entries(summary.properties)) data.set(`items[0][properties][${key}]`,value);
    // Distinguish configurations across separate adds of the same base variant.
    data.set('items[0][properties][_Configuration]',group);
    summary.addons.forEach((addon,index)=>{
      const prefix=`items[${index+1}]`;
      data.set(`${prefix}[id]`,addon.id);data.set(`${prefix}[quantity]`,String(quantity));
      data.set(`${prefix}[parent_id]`,variantId);
      data.set(`${prefix}[properties][For]`,productTitle);
      data.set(`${prefix}[properties][Option]`,`${addon.field}: ${addon.value}`);
      data.set(`${prefix}[properties][_Configuration]`,group);
    });
    return data;
  }
  const core={presets,parseOptions,buildFields,resolveOption,selectionSummary,buildCartData,money};
  if(typeof module!=='undefined' && module.exports) module.exports=core;
  if(typeof window==='undefined' || !window.customElements || customElements.get('v-configurable-product-form')) return;

  const el=(tag,className,text)=>{
    const node=document.createElement(tag);
    if(className) node.className=className;
    if(text!==undefined) node.textContent=text;
    return node;
  };
  class ProductOptionsForm extends HTMLElement {
    connectedCallback() {
      if(this.ready) return;
      this.ready=true;
      this.failed=false;
      this.error=this.querySelector('[data-cpf-error]');
      this.abort=new AbortController();
      try {
        this.config=JSON.parse(this.querySelector('[data-cpf-config]').textContent);
        this.form=document.getElementById(this.config.formId);
        this.section=document.getElementById(`shopify-section-${this.config.sectionId}`) || this.closest('section');
        if(!this.form || !this.section) throw new Error('Product form unavailable. Reload the page.');
        this.fields=buildFields(this.config);
        this.renderFields();
        this.onSubmit=event=>{
          if(event.target!==this.form) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submit(event.submitter);
        };
        document.addEventListener('submit',this.onSubmit,{capture:true,signal:this.abort.signal});
        this.addEventListener('input',()=>this.update(),{signal:this.abort.signal});
        this.addEventListener('change',event=>{
          // A free "Nope"/"None" choice excludes the paid underscrub choices.
          const group=event.target.closest('[data-cpf-field]');
          if(group && event.target.type==='checkbox' && event.target.checked) {
            const field=this.fields.find(f=>f.index===Number(group.dataset.cpfField));
            const none=o=>/^(no|nope|none)$/i.test(o.label) && o.amount===0 && !o.variantId;
            const selected=field.options[Number(event.target.value)];
            group.querySelectorAll('input').forEach(input=>{
              if(input!==event.target && (none(selected) || none(field.options[Number(input.value)]))) input.checked=false;
            });
          }
          this.update();
        },{signal:this.abort.signal});
        // Minimog can change variant input.value without dispatching a DOM change event.
        this.timer=setInterval(()=>this.update(),250);
        this.priceObserver=new MutationObserver(()=>this.updatePrice());
        this.section.querySelectorAll('.product-block-group--price').forEach(node=>this.priceObserver.observe(node,{childList:true,subtree:true}));
        this.canSubmit=true;
        this.update();
        this.form.querySelectorAll('[data-cpf-submit]').forEach(button=>button.disabled=!this.currentVariant()?.available);
      } catch(error) {
        this.failed=true;
        this.showError(this.config?.designMode ? error.message : 'This product form is unavailable. Please contact the store.');
        // A broken configuration must not silently submit a bare product.
        if(this.form) document.addEventListener('submit',event=>{
          if(event.target===this.form){event.preventDefault();event.stopImmediatePropagation();this.error.scrollIntoView({block:'center'});}
        },{capture:true,signal:this.abort.signal});
      }
    }
    disconnectedCallback() {
      this.abort?.abort();clearInterval(this.timer);this.priceObserver?.disconnect();this.ready=false;this.canSubmit=false;
    }
    currentVariant() {
      const id=this.form.querySelector('[name="id"]')?.value;
      return this.config.variants.find(v=>String(v.id)===id);
    }
    showError(message) {this.error.textContent=message;this.error.hidden=!message;}
    renderFields() {
      const root=this.querySelector('[data-cpf-fields]');root.replaceChildren();
      const warnings=[];
      this.fields.forEach(field=>{
        const group=el('fieldset','cpf-field');group.dataset.cpfField=field.index;
        const legend=el('legend','cpf-label',field.label);
        if(field.required) legend.append(el('span','cpf-required','*'));
        group.append(legend);
        const id=`cpf-${this.config.blockId}-${field.index}`;
        const textField=['text','textarea'].includes(field.type);
        field.options.forEach(option=>{const resolved=resolveOption(option,this.config);if(resolved.error) warnings.push(`${field.label} / ${option.label}: ${resolved.error}`);});
        const control=node=>{
          node.setAttribute('form',this.config.formId);
          node.id=id;
          node.setAttribute('aria-label',field.label);
          if(field.required) node.required=true;
          return node;
        };
        if(textField) {
          const fee=resolveOption(field.options[0],this.config);
          if(fee.price) legend.append(el('span','cpf-fee',money(fee.price,this.config.currency)));
          const input=control(el(field.type==='textarea'?'textarea':'input','cpf-input'));
          if(field.type==='text') input.type='text';
          input.maxLength=field.type==='textarea'?2000:150;
          input.placeholder=field.placeholder;
          group.append(input);
        } else if(field.type==='select') {
          const select=control(el('select','cpf-input'));
          const placeholder=el('option','',field.placeholder || '-- Please select --');placeholder.value='';select.append(placeholder);
          field.options.forEach((option,i)=>{
            const resolved=resolveOption(option,this.config);
            const item=el('option','',option.label+(resolved.price?` (+${money(resolved.price,this.config.currency)})`:''));
            item.value=String(i);select.append(item);
          });group.append(select);
        } else {
          const choices=el('div',field.type==='images'?'cpf-images':'cpf-choices');
          field.options.forEach((option,i)=>{
            const resolved=resolveOption(option,this.config);
            const label=el('label',field.type==='images'?'cpf-image-choice':'cpf-choice');
            const input=control(el('input'));
            input.id=`${id}-${i}`;
            input.removeAttribute('aria-label'); // The wrapping label supplies each distinct option name.
            input.type=field.type==='checkbox'?'checkbox':'radio';
            input.name=`cpf-choice-${this.config.blockId}-${field.index}`;
            input.value=String(i);
            if(field.type==='checkbox') input.required=false; // group requirement is checked together
            label.append(input);
            if(field.type==='images') {
              const box=el('span','cpf-image-box');
              if(option.image) {
                const image=el('img');image.src=option.image;image.alt=option.label;image.loading='lazy';image.width=160;image.height=160;box.append(image);
              } else box.append(el('span','cpf-image-placeholder',option.label));
              label.append(box);
            }
            label.append(el('span','cpf-choice-text',option.label+(resolved.price?` (+${money(resolved.price,this.config.currency)})`:'')));
            choices.append(label);
          });group.append(choices);
        }
        // Apply a configured default once, when this field is first rendered.
        if(!textField && Number.isInteger(field.defaultOption) && field.defaultOption>0 && field.defaultOption<=field.options.length) {
          const optionIndex=field.defaultOption-1;
          if(field.type==='select') group.querySelector('select').value=String(optionIndex);
          else group.querySelectorAll('input')[optionIndex].checked=true;
        }
        root.append(group);
      });
      if(this.config.designMode && warnings.length) {
        const note=this.querySelector('[data-cpf-editor]');note.hidden=false;
        note.textContent='Finish setting up paid options before publishing:\n'+warnings.join('\n');
      }
    }
    selections(validate=false) {
      return this.fields.map(field=>{
        const group=this.querySelector(`[data-cpf-field="${field.index}"]`);
        const textField=['text','textarea'].includes(field.type);
        let values=[],text='';
        if(textField) {
          const input=group.querySelector('input,textarea');text=input.value.trim();
          input.setCustomValidity(validate && field.required && !text?'Please complete this field.':'');
          if(text) values=[field.options[0]];
        } else if(field.type==='select') {
          const selected=group.querySelector('select').value;
          if(selected!=='') values=[field.options[Number(selected)]];
        } else {
          values=[...group.querySelectorAll('input:checked')].map(input=>field.options[Number(input.value)]);
          if(field.type==='checkbox') group.querySelector('input').setCustomValidity(validate && field.required && !values.length?'Please select at least one option.':'');
        }
        return {field,values,text};
      });
    }
    update() {
      if(this.failed || this.busy) return;
      try {
        const selections=this.selections();
        this.summary=selectionSummary(selections,this.config);
        this.selectionError='';
      } catch(error) {
        this.selectionError=error.message;
        this.summary=null;
      }
      this.updatePrice();
      this.form.querySelectorAll('[data-cpf-submit]').forEach(button=>button.disabled=!this.currentVariant()?.available || !!this.uncertain);
      const note=this.querySelector('[data-cpf-summary]');
      const surcharge=this.summary?.surcharge || 0;
      const text=this.selectionError || (surcharge?`Options: +${money(surcharge,this.config.currency)} per item. Paid options appear as linked items in your cart.`:'');
      if(note.textContent!==text) note.textContent=text;
    }
    updatePrice() {
      const variant=this.currentVariant();if(!variant) return;
      const total=variant.price+(this.summary?.surcharge || 0);
      const display=money(total,this.config.currency);
      // Update the actual current-price nodes; leave compare-at prices, badges and unrelated blocks alone.
      this.section.querySelectorAll('.product-block-group--price .m-price__regular .m-price-item, .product-block-group--price .m-price__sale .m-price-item--sale, .product-block-group--price .m-price__sale .m-price-item--last').forEach(node=>{
        if(!node.matches('s,del') && !node.closest('s,del') && node.textContent!==display) node.textContent=display;
      });
    }
    async submit(submitter) {
      if(this.busy || this.uncertain) return;
      this.showError('');
      const selections=this.selections(true);
      if(!this.form.reportValidity()) return;
      const variant=this.currentVariant();
      if(!variant?.available) return this.showError('Please select an available product variant.');
      let summary;
      try{summary=selectionSummary(selections,this.config);}catch(error){return this.showError(error.message);}
      const original=new FormData(this.form);
      if(original.get('selling_plan')) return this.showError('These customization options are available for one-time purchases only.');
      const quantity=Number(original.get('quantity') || this.section.querySelector('[name="quantity"]')?.value || 1);
      if(!Number.isSafeInteger(quantity) || quantity<1) return this.showError('Please enter a valid quantity.');
      const group=window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const data=buildCartData(original,variant.id,quantity,summary,this.config.productTitle,group);
      const drawer=document.querySelector('m-cart-drawer');
      let sections=[];
      try{sections=drawer?.getSectionsToRender?.() || [];}catch(error){/* use cart page fallback */}
      if(sections.length) {
        data.set('sections',[...new Set(sections.map(s=>s.id))].slice(0,5).join(','));
        data.set('sections_url',window.location.pathname);
      }
      const buttons=[...this.form.querySelectorAll('[type="submit"]')];
      this.busy=true;this.form.setAttribute('aria-busy','true');buttons.forEach(b=>b.disabled=true);
      const root=window.Shopify?.routes?.root || '/';
      let added;
      try {
        const response=await fetch(root+'cart/add.js',{method:'POST',headers:{Accept:'application/json'},body:data});
        const result=await response.json();
        if(!response.ok) {
          // Shopify may partially apply a multi-item request. Never silently retry after an error.
          this.uncertain=true;
          this.showError((typeof result.description==='string'?result.description:'Unable to add all selected items.')+' Please review your cart before trying again.');
          this.appendCartLink(root);
          return;
        }
        added=result;
      } catch(error) {
        this.uncertain=true;
        this.showError('We could not confirm whether your product was added. Review your cart before trying again to avoid duplicates.');
        this.appendCartLink(root);
        return;
      } finally {
        this.busy=false;this.form.removeAttribute('aria-busy');
        buttons.forEach(button=>button.disabled=!!this.uncertain || !this.currentVariant()?.available);
      }
      // Adding succeeded. Rendering failures never retry the cart mutation.
      try {
        if(!drawer?.renderContents || !sections.length || !sections.every(s=>typeof added.sections?.[s.id]==='string')) throw new Error('Use cart page');
        const parent=added.items?.find(item=>!item.parent_relationship) || added.items?.[0] || added;
        drawer.setActiveElement?.(submitter);
        await drawer.renderContents({...parent,sections:added.sections});
        if(drawer.getCart && drawer.updateCartCount) {
          const cart=await drawer.getCart();drawer.updateCartCount(cart.item_count);
        }
      } catch(error) {window.location.assign(root+'cart');}
    }
    appendCartLink(root) {
      const link=el('a','',' View cart');link.href=root+'cart';link.style.textDecoration='underline';this.error.append(link);
    }
  }
  customElements.define('v-configurable-product-form',ProductOptionsForm);
})();
